import {
    ReadonlyVec3,
    ReadonlyVec4,
    mat4,
    quat,
    vec2,
    vec3,
    vec4,
} from 'gl-matrix';
import { TextureView } from '../../../view/TextureView';
import { ContourTexture } from '../../structure/ContourTexture';
import { RotatedBoundingBox } from '../RotatedBoundingBox';
import { CustomTargetObject } from './CustomTargetObject';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { PlaneView } from '../../../view/views/PlaneView';
import { gl } from '../../../Engine';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { UniformVec4 } from '../../../core/utils/uniforms/UniformVec4';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformVec2 } from '../../../core/utils/uniforms/UniformVec2';
import { ImageObject } from '../../image/ImageObject';

/**
 * {@link TargetSphere} is a {@link CustomTargetObject} that represents a sphere in 3D space.\
 * It is defined by a center point and a diameter in millimeters.\
 * The mesh of the {@link TargetSphere} aligns with the {@link target} to simulate the actual result of a brush stroke.
 */
export class TargetSphere extends CustomTargetObject {
    /**
     * The outline width in pixels.
     */
    private static readonly OUTLINE_WIDTH = 1;

    private readonly margin = ContourTexture.DEFAULT_MARGIN;

    /**
     * Diameter of the sphere in millimeters.
     */
    protected diameter: number;

    /**
     * {@link RotatedBoundingBox} that defines the dimensions of the sphere.\
     * The scale is 0.5 * diameter + margin.
     */
    protected readonly _boundingBox: RotatedBoundingBox;

    /**
     * {@link FrameBufferObject} used to update the {@link sdf} {@link WebGLTexture}.
     */
    protected fbo?: FrameBufferObject;

    /**
     * {@link SphereShader} used to render the sphere sdf data on the {@link sdf} {@link WebGLTexture}.
     */
    protected readonly shader: SphereShader;

    /**
     * {@link OutlineShader} used to render the sphere outline.\
     * This works similarly to {@link ContourRenderer} in that it reads the {@link sdf} {@link WebGLTexture} within the fragment shader.\
     * It then checks if the current pixel is within the sphere and if it is, it checks if any of the neighboring pixels are outside the sphere.\
     * If any of the neighboring pixels are outside the sphere, the current pixel is colored .
     */
    private readonly outlineShader: OutlineShader;

    /**
     * {@link mat4} that converts from patient space to image space.\
     * This is used by the {@link outlineShader} to convert pixels from ViewSpace to TextureSpace.\
     * This conversion is performed to convert pixels from ViewSpace to {@link sdf} TextureSpace to see if the pixel is on the outline.
     */
    private readonly patientToImage: mat4;

    /**
     * {@link vec4 Color} of the sphere's outline.
     */
    private readonly color: vec4;

    /**
     * Constructor for the {@link TargetSphere} class.\
     * This constructor calls the {@link CustomTargetObject} constructor and initializes the {@link sdf}.\
     * It also initializes the {@link shader} and {@link outlineShader} and generates the initial {@link intersectionMesh mesh}.
     * @param target {@link SliceSource} defining the space that this {@link CustomTargetObject} might intersect with.
     * @param center {@link vec3 Vector} defining the center of the sphere in patient space.
     * @param diameter Diameter of the sphere in millimeters.
     * @param color {@link vec4 Color} of the sphere's outline.
     */
    public constructor(
        target: ImageObject,
        center: ReadonlyVec3,
        diameter: number,
        color: ReadonlyVec4
    ) {
        super(target);
        this.patientToImage = mat4.create();
        this.diameter = diameter;
        this.color = vec4.clone(color);
        this._boundingBox = new RotatedBoundingBox();
        this._boundingBox.expandWithPoint(center);
        this._boundingBox.addMargins(0.5 * diameter + this.margin);
        this.shader = new SphereShader();
        this.outlineShader = new OutlineShader();
        this.updateTexture();
    }

    /**
     * Method to render the outline of this {@link TargetSphere} on a {@link view}.
     * @throws Error if the {@link view} is not a {@link PlaneView}.
     * @param view {@link TextureView} on which the {@link TargetSphere} should be rendered.
     */
    public render(view: TextureView): void {
        if (view instanceof PlaneView) {
            if (this.sdf) {
                const vao = this._boundingBox.crossSection(
                    view.projectionViewMatrix
                );
                if (vao) {
                    this.outlineShader.start();
                    this.outlineShader.colorMask.loadVec4(this.color);
                    this.outlineShader.relativeDist.loadVec2(
                        vec2.fromValues(
                            TargetSphere.OUTLINE_WIDTH / 2 / view.size[0],
                            TargetSphere.OUTLINE_WIDTH / 2 / view.size[1]
                        )
                    );

                    const viewToPatient = mat4.invert(
                        mat4.create(),
                        view.projectionViewMatrix
                    );
                    //remove translation
                    viewToPatient[12] =
                        viewToPatient[13] =
                        viewToPatient[14] =
                            0.0;
                    this.outlineShader.viewToTexture.loadMatrix(
                        mat4.multiply(
                            mat4.create(),
                            this.patientToImage,
                            viewToPatient
                        )
                    );
                    this.sdf.bind();
                    vao.bind(0, 1);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
        } else {
            throw new Error(
                `Attempt to render ${this.constructor.name} on unsupported View of type ${view.constructor.name}`
            );
        }
    }

    /**
     * Method to get the {@link _boundingBox RotatedBoundingBox} of this {@link TargetSphere}.
     */
    public get boundingBox(): RotatedBoundingBox {
        return this._boundingBox;
    }

    /**
     *
     * @param size
     */
    public resize(size: number): void {
        this.diameter = size;
        const bb = new RotatedBoundingBox();
        bb.expandWithPoint(this._boundingBox.center);
        bb.rotate(this._imageGrid.getOrientation());
        bb.addMargins(0.5 * size + this.margin);
        this._boundingBox.set(bb);
        this.updateTexture();
    }

    public setPosition(position: ReadonlyVec3): void {
        this._boundingBox.translate(
            vec3.sub(vec3.create(), position, this._boundingBox.center)
        );
        this.updateTexture();
    }

    /**
     * Method to update the {@link sdf} {@link WebGLTexture} of this {@link TargetSphere}.\
     * This method is called by the {@link CustomTargetObject.update update} method of the {@link CustomTargetObject} superclass\
     * after the {@link intersectionMesh} has been {@link updateIntersectionMesh updated}.
     */
    protected updateTexture() {
        if (!this._boundingBox.min || !this._boundingBox.max) {
            return;
        }
        const planarPixelSize = vec2.fromValues(
            this.boundingBox.size[0],
            this.boundingBox.size[1]
        );
        vec2.scale(planarPixelSize, planarPixelSize, 1 / this._imageGrid.resX);
        vec2.ceil(planarPixelSize, planarPixelSize);
        const boundingBoxSlices =
            Math.floor(
                this._boundingBox.size[2] / this._targetImage.parent.grid.resZ
            ) + 2;

        const pixelSize = vec3.fromValues(
            planarPixelSize[0],
            planarPixelSize[1],
            boundingBoxSlices
        );
        if (this.sdf) {
            if (!vec3.equals(this.sdf.size, pixelSize)) {
                this.sdf.resize(pixelSize);
            }
        } else {
            this.sdf = new SignedDistanceField(pixelSize);
        }
        if (this.fbo) {
            if (!vec2.equals(this.fbo.size, planarPixelSize)) {
                this.fbo.resize(planarPixelSize);
            }
        } else {
            this.fbo = new FrameBufferObject(planarPixelSize);
        }
        this.fbo.bind();
        const vao = RenderStorage.getQuadVAO();
        vao.bind(0);
        gl.viewport(0, 0, this.sdf.size[0], this.sdf.size[1]);
        this.shader.start();
        const totalSize = 2 * this.margin + this.diameter;
        this.shader.margin.loadFloat((2 * this.margin) / totalSize);
        this.shader.radius.loadFloat(this.diameter / totalSize);

        const bbBottom = this._targetImage.parent.grid.toVoxelSpace(
            this._boundingBox.min
        );
        const bbBottomSlicePos = this._targetImage.parent.grid.toWorldSpace(
            vec3.floor(vec3.create(), bbBottom)
        );

        const bbTop = this._targetImage.parent.grid.toVoxelSpace(
            this._boundingBox.max
        );
        const bbTopSlicePos = this._targetImage.parent.grid.toWorldSpace(
            vec3.ceil(vec3.create(), bbTop)
        );

        const bbBottomSliceNormalPos = bbBottomSlicePos[2];
        const bbTopSliceNormalPos = bbTopSlicePos[2];

        const imageToPatient = mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.fromMat3(quat.create(), this._boundingBox.rotation),
            vec3.create(),
            vec3.scale(vec3.create(), this._boundingBox.size, 0.5)
        );
        mat4.invert(this.patientToImage, imageToPatient);
        this.patientToImage[12] =
            this.patientToImage[13] =
            this.patientToImage[14] =
                0;

        const centerNormal = vec3.transformMat3(
            vec3.create(),
            this._boundingBox.center,
            this._boundingBox.invRotation
        )[2];
        for (let i = 0; i < this.sdf.size[2]; ++i) {
            this.fbo.createTextureLayer(this.sdf.texture, i);
            gl.clear(gl.COLOR_BUFFER_BIT);
            const normalPosition =
                bbBottomSliceNormalPos + i * this._targetImage.parent.grid.resZ;
            const deltaNormal = normalPosition - centerNormal;
            if (Math.abs(deltaNormal) < 0.5 * this.diameter + this.margin) {
                //render sphere
                this.shader.normalDistance.loadFloat(
                    deltaNormal < 0
                        ? deltaNormal / (centerNormal - bbBottomSliceNormalPos)
                        : deltaNormal / (bbTopSliceNormalPos - centerNormal)
                );
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        }
        this.shader.stop();
        vao.unbind(0);
        this.fbo.unbind();
    }

    /**
     * @Override
     * Method to delete this {@link CustomTargetObject}.\
     * This calls the {@link CustomTargetObject.delete delete} method of the {@link CustomTargetObject} superclass, which deletes the {@link sdf}.\
     * But it also {@link FrameBufferObject.delete deletes} the {@link fbo} and {@link shader}.\
     * It is important to call this method when the {@link TargetSphere} is no longer needed.
     * @returns True to indicate that the {@link CustomTargetObject} was successfully deleted.
     */
    public delete(): boolean {
        super.delete();
        this.shader.delete();
        this.outlineShader.delete();
        if (this.fbo) {
            this.fbo.delete();
        }
        return true;
    }
}

/**
 * {@link SphereShader} is a {@link ShaderProgram} that is used to render the {@link sdf} {@link WebGLTexture} of a {@link TargetSphere}.\
 * It renders the {@link sdf} {@link WebGLTexture} as a sphere with a given {@link radius} and {@link margin}.
 */
class SphereShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;

    out vec2 textureCoords;

    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        textureCoords = position;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;

    //Value in between -1 and 1
    in vec2 textureCoords;

    out float outColor;

    uniform float normalDistance;
    uniform float radius;
    uniform float margin;

    void main(){
        float distance = length(vec3(textureCoords, normalDistance));
        outColor = 0.5 - 0.5 * (distance - radius) / margin;
    }
    `;

    /**
     * Radius relative to texture size.\
     * radius is the distance at which values become 0.5.\
     * This should be half of the brush diameter.
     */
    public readonly radius: UniformFloat;

    /**
     * Margin relative to texture size.\
     * Margin is the distance from brush edge, at which the values turn to either 0 or 1.
     */
    public readonly margin: UniformFloat;

    /**
     * Distance of the current slice from the center of the sphere.\
     * This distance should be a value in between -1 and 1.
     */
    public readonly normalDistance: UniformFloat;

    public constructor() {
        super(
            SphereShader.VERTEX_CONTENT,
            SphereShader.FRAGMENT_CONTENT,
            'position'
        );
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.normalDistance = new UniformFloat('normalDistance');
        this.storeUniformLocations(
            this.radius,
            this.margin,
            this.normalDistance
        );
    }
}

/**
 *
 */
class OutlineShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;
    in vec3 textureCoords;

    out vec3 pass_textureCoordinates;

    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoordinates = textureCoords;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;

    out vec4 outColor;

    uniform vec4 colorMask;
    uniform sampler3D sampler;

    const float diagonal = 0.70710678118;
    uniform mat4 viewToTexture;
    uniform vec2 relativeDist;

    bool isWithin(float x, float y) {
        vec4 rotated = viewToTexture * vec4(x * relativeDist.x, y * relativeDist.y, 0.0, 1.0);
        vec3 dest = pass_textureCoordinates + rotated.xyz;
        if(dest.x <= 0.0 || dest.x >= 1.0 || dest.y <= 0.0 || dest.y >= 1.0 || dest.z <= 0.0 || dest.z >= 1.0){
            return false;
        }
        return texture(sampler, dest).r >= 0.5;
    }

    void main(){
        bool within = texture(sampler, pass_textureCoordinates).r >= 0.5;

        if(
        isWithin(-diagonal, -diagonal) != within ||
        isWithin(0.0, -1.0) != within ||
        isWithin(diagonal, -diagonal) != within ||
        isWithin(-1.0, 0.0) != within ||
        isWithin(1.0, 0.0) != within ||
        isWithin(-diagonal, diagonal) != within ||
        isWithin(0.0, 1.0) != within ||
        isWithin(diagonal, diagonal) != within){
            outColor = colorMask;
        } else {
            //outColor = texture(sampler, pass_textureCoordinates);
            discard;
        }
    }
    `;

    /**
     * {@link UniformVec4} that takes the line color as an input and provides it to {@link ContourTextureShader}.\
     * That color value is used as is to color the contour outlines.
     */
    public readonly colorMask: UniformVec4;

    /**
     * {@link UniformMatrix} that provides a {@link ReadonlyMat4 Matrix} to convert distances from ViewSpace to TextureSpace.\
     * This is used to convert the {@link ContourRenderer.lineWidth lineWidth} into texture units.
     */
    public readonly viewToTexture: UniformMatrix;

    /**
     * {@link UniformVec2} that provides a {@link ReadonlyVec2 Vector} which represents the {@link ContourRenderer.lineWidth lineWidth} in ViewSpace.
     */
    public readonly relativeDist: UniformVec2;

    constructor() {
        super(
            OutlineShader.VERTEX_CONTENT,
            OutlineShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.colorMask = new UniformVec4('colorMask');
        this.viewToTexture = new UniformMatrix('viewToTexture');
        this.relativeDist = new UniformVec2('relativeDist');
        this.storeUniformLocations(
            this.colorMask,
            this.viewToTexture,
            this.relativeDist
        );
    }
}
